import { Alert, Stack } from '@mui/material';
import { t } from 'i18next';
import { FormEventHandler, useState } from 'react';
import { getErrorMessage } from '../../common/errors/errors';
import { useFormItem } from '../../common/hooks/use-form-item';
import { InputType } from '../../common/hooks/use-form-item/types';
import { and } from '../../common/hooks/use-form-item/validators/compositions';
import { required } from '../../common/hooks/use-form-item/validators/existential';
import { maxLength } from '../../common/hooks/use-form-item/validators/operational';
import { SlotType } from '../models/SlotType';
import { ButtonBox, PrimaryButton, TertiaryButton } from './Buttons';

type BaseProps = {
  submit: {
    label: string;
    action: (name: string, description: string, slotType: string, tags: string[]) => Promise<void>;
  };
  back: {
    label: string;
    action: () => void;
  };
};

type UpdateModeProps = BaseProps & {
  prefill: {
    name: string;
    description: string | null;
    tags: string[];
    slotType: SlotType;
  };
  slotTypes?: SlotType[];
};

type CreateModeProps = BaseProps & {
  prefill?: {
    name: string;
    description: string | null;
    tags: string[];
    slotType: SlotType;
  };
  slotTypes: SlotType[];
};

type Props = UpdateModeProps | CreateModeProps;

export const SlotForm = ({ prefill, submit, back, slotTypes }: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Hide the slot type input when the form is being used to edit the slot
  const showTypeInput = !prefill;

  const [name, nameComponent, isNameValid] = useFormItem<string>({
    type: InputType.TYPE_TEXT,
    label: t(`slotForm.slot.name`),
    value: prefill?.name || '',
    validator: and(required(), maxLength(1000)),
    inputOptions: {
      autoFocus: true,
      disabled: loading,
      showRequiredIndicator: true,
    },
    textOptions: { trimOnBlur: true },
  });

  const [description, descriptionComponent, isDescriptionValid] = useFormItem<string>({
    type: InputType.TYPE_TEXT,
    label: t(`slotForm.slot.description`),
    value: prefill?.description || '',
    validator: maxLength(1000),
    inputOptions: {
      disabled: loading,
    },
    textOptions: { trimOnBlur: true },
  });

  const [tags, tagsComponent] = useFormItem<string[]>({
    type: InputType.TYPE_FREE_TAGS,
    label: t(`slotForm.tags.input`),
    value: prefill?.tags || [],
    validator: null,
    inputOptions: {
      disabled: loading,
      focusedLabel: t('slotForm.tags.focused'),
    },
  });

  const [slotType, slotTypeComponent, isSlotTypeValid] = useFormItem<string>({
    type: InputType.TYPE_AUTOCOMPLETE,
    label: t(`slotForm.slot.type.input`),
    value: prefill?.slotType?.getValue() || '',
    validator: showTypeInput ? required() : null,
    inputOptions: {
      disabled: loading || !!prefill?.slotType,
      focusedLabel: t(`slotForm.slot.type.focused`),
      showRequiredIndicator: true,
    },
    autocompleteOptions: {
      readOnly: true,
      clearable: false,
      options: slotTypes?.map((a) => ({ value: a.getValue(), label: a.getLabel(), disabled: !a.isAllowed() })),
    },
  });

  const canSubmit = isNameValid && (!showTypeInput || isSlotTypeValid) && isDescriptionValid && !loading;

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      setLoading(true);
      setError(null);
      await submit.action(name, description, slotType, tags);
    } catch (e: unknown) {
      setError(getErrorMessage(e));
      setLoading(false);
    }
  };

  return (
    <Stack spacing={1.5} component="form" onSubmit={onSubmit}>
      {error && <Alert severity="error">{error}</Alert>}
      {nameComponent}
      {descriptionComponent}
      {tagsComponent}
      {showTypeInput && slotTypeComponent}
      <ButtonBox>
        <TertiaryButton disabled={loading} onClick={back.action}>
          {back.label}
        </TertiaryButton>
        <PrimaryButton disabled={!canSubmit} type="submit">
          {submit.label}
        </PrimaryButton>
      </ButtonBox>
    </Stack>
  );
};
